import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { config } from '../config'
import { useAuth } from './AuthContext'
import { queryClient } from '../lib/queryClient'

interface SocketContextType {
	socket: Socket | null
	isConnected: boolean
	emitTripUpdate: (tripId: string) => void
	emitItineraryUpdate: (tripId: string) => void
	joinTrip: (tripId: string) => void
	leaveTrip: (tripId: string) => void
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
	children: ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
	const [socket, setSocket] = useState<Socket | null>(null)
	const [isConnected, setIsConnected] = useState(false)
	const { token, user } = useAuth()

	useEffect(() => {
		if (token && user) {
			// Get the base URL without /api/v1 for Socket.IO
			const socketURL = config.apiUrl

			// Initialize Socket.IO connection
			const newSocket = io(socketURL, {
				auth: {
					token,
				},
				transports: ['websocket', 'polling'],
				autoConnect: true, // Explicitly enable auto-connect
			})

			// Connection event handlers
			newSocket.on('connect', () => {
				console.log('✅ Socket.IO connected:', newSocket.id)
				setIsConnected(true)
			})

			newSocket.on('disconnect', reason => {
				console.log('❌ Socket.IO disconnected:', reason)
				setIsConnected(false)
			})

			newSocket.on('connect_error', error => {
				console.error('🚨 Socket.IO connection error:', error)
				setIsConnected(false)
			})

			newSocket.io.on('reconnect', () => {
				console.log('🔄 Socket.IO reconnected, invalidating queries...')
				queryClient.invalidateQueries()
			})

			newSocket.on('connected', data => {
				console.log('✅ Socket.IO authenticated:', data)
			})

			newSocket.on('error', error => {
				console.error('🚨 Socket.IO error:', error)
			})

			// Trip-related event handlers
			newSocket.on('trip_updated', data => {
				console.log('📢 Trip updated:', data)
				window.dispatchEvent(new CustomEvent('trip_updated', { detail: data }))
			})

			newSocket.on('itinerary_updated', data => {
				console.log('📢 Itinerary updated:', data)
				window.dispatchEvent(new CustomEvent('itinerary_updated', { detail: data }))
			})

			newSocket.on('travel_segments_updated', data => {
				console.log('📢 Travel segments updated:', data)
				window.dispatchEvent(new CustomEvent('travel_segments_updated', { detail: data }))
			})

			setSocket(newSocket)

			// Explicitly connect
			if (!newSocket.connected) {
				console.log('🔌 Manually connecting socket...')
				newSocket.connect()
			}

			// Cleanup on unmount
			return () => {
				console.log('🧹 Cleaning up socket connection')
				newSocket.close()
			}
		} else if (socket) {
			console.log('🚪 Logging out, closing socket')
			socket.close()
			setSocket(null)
			setIsConnected(false)
		}
	}, [token, user])

	const emitTripUpdate = useCallback(
		(tripId: string) => {
			if (socket) {
				socket.emit('trip_update', { tripId })
			}
		},
		[socket],
	)

	const emitItineraryUpdate = useCallback(
		(tripId: string) => {
			if (socket) {
				socket.emit('itinerary_update', { tripId })
			}
		},
		[socket],
	)

	const joinTrip = useCallback(
		(tripId: string) => {
			if (socket && isConnected) {
				socket.emit('join_trip', { tripId })
				console.log(`🔗 Joined trip room: trip:${tripId}`)
			}
		},
		[socket, isConnected],
	)

	const leaveTrip = useCallback(
		(tripId: string) => {
			if (socket && isConnected) {
				socket.emit('leave_trip', { tripId })
				console.log(`🔌 Left trip room: trip:${tripId}`)
			}
		},
		[socket, isConnected],
	)

	const value: SocketContextType = useMemo(
		() => ({
			socket,
			isConnected,
			emitTripUpdate,
			emitItineraryUpdate,
			joinTrip,
			leaveTrip,
		}),
		[socket, isConnected, emitTripUpdate, emitItineraryUpdate, joinTrip, leaveTrip],
	)

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocket = (): SocketContextType => {
	const context = useContext(SocketContext)
	if (context === undefined) {
		throw new Error('useSocket must be used within a SocketProvider')
	}
	return context
}
